import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SelectFieldProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label: string;
  /** Visually hide the label (still read by screen readers). */
  hideLabel?: boolean;
  size?: 'default' | 'sm';
}

/** Native drop-down list with the Europa chevron: appearance none, chevron 14px (12px small) from the right edge. */
export const SelectField = React.forwardRef<HTMLSelectElement, SelectFieldProps>(
  ({ label, hideLabel = false, size = 'default', id, className, children, ...props }, ref) => {
    const generatedId = React.useId();
    const selectId = id ?? generatedId;
    return (
      <div className="flex items-center gap-2.5">
        <label htmlFor={selectId} className={cn('text-[13px] text-foreground-secondary', hideLabel && 'sr-only')}>
          {label}
        </label>
        <span className="relative flex">
          <select
            ref={ref}
            id={selectId}
            className={cn(
              'flex-1 cursor-pointer appearance-none rounded-control border border-input bg-card font-[inherit] text-foreground',
              size === 'default' ? 'min-h-10 min-w-[210px] pl-3 pr-10 text-sm' : 'min-h-8 pl-2.5 pr-8 text-[13px]',
              className,
            )}
            {...props}
          >
            {children}
          </select>
          <ChevronDown
            aria-hidden="true"
            className={cn('pointer-events-none absolute top-1/2 -translate-y-1/2 text-foreground-secondary', size === 'default' ? 'right-3.5 size-4' : 'right-3 size-3.5')}
          />
        </span>
      </div>
    );
  },
);
SelectField.displayName = 'SelectField';
